import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { disabledConvertMessages } from '../../shared/enums';
import { Program } from '@finpay-payment-v3/payment-types';
import { StripePaymentMethod } from '../../patient/components/models/stripe-payment-method';
import { emailAddressValidator } from '../../shared/misc/regex';
import {
  checkIOCComplete,
  checkAccountHolderAddress,
  checkPFRMatch,
  checkNotSkeletonProgram,
  checkDownPaymentWillBeSatisfied,
  checkRecurringPaymentDate,
  checkRecurringPaymentDateNotPast,
  checkRecurringPaymentDateWithin60Days,
  checkRecurringPaymentDateValidDay,
  checkRecurringPaymentMethodExists,
  NewGuarantorFormData,
  ExistingGuarantorFormData,
  getMissingAccountHolderFields,
  getMissingIOCFields,
  checkDemographicsCompleteWithFormData,
  getMissingDemographicFieldsWithFormData,
} from './validation-checks';

export interface PreEnrollRequirement {
  requirementName: string;
  status: boolean;
  missingFields?: string[];
  fixUrl?: string;
  fixLabel?: string;
}

interface PreEnrollValidationParams {
  encounter: PatientEncounter | null;
  patient: PatientViewModel | null;
  program?: Program | any;
  paymentMethods?: StripePaymentMethod[];
  isDownPaymentFullyPaid?: boolean;
  isLoading?: boolean;
  recurringPaymentMethod?: StripePaymentMethod | null;
  hasRecurringMethod?: boolean;
  currentPaymentAmount?: number;
  pendingRemainingBalance?: number;
  recurringPaymentDate?: string;
  isCreatingNewGuarantor?: boolean;
  newGuarantorData?: NewGuarantorFormData;
  existingGuarantorFormData?: ExistingGuarantorFormData;
  accountHolderName?: string;
  patientId?: number;
  encounterId?: number;
  billingEmail?: string;
  isPaymentMethodValid?: boolean;
}

interface PreEnrollValidationResult {
  isValid: boolean;
  requirements: PreEnrollRequirement[];
  failedRequirements: PreEnrollRequirement[];
}

/**
 * Check if billing/receipt email is valid.
 */
const checkBillingEmailComplete = (email?: string): boolean => {
  if (!email || email.trim() === '') return false;
  return emailAddressValidator.test(email);
};

/**
 * Get missing billing email field description.
 */
const getMissingBillingEmailFields = (email?: string): string[] => {
  if (!email || email.trim() === '') return ['Receipt Email'];
  if (!emailAddressValidator.test(email)) return ['Valid Receipt Email'];
  return [];
};

export const validatePreEnrollRequirements = (
  params: PreEnrollValidationParams
): PreEnrollValidationResult => {
  const {
    encounter,
    patient,
    isDownPaymentFullyPaid = false,
    isLoading = false,
    recurringPaymentMethod,
    hasRecurringMethod = true,
    currentPaymentAmount = 0,
    pendingRemainingBalance,
    recurringPaymentDate,
    isCreatingNewGuarantor,
    newGuarantorData,
    existingGuarantorFormData,
    accountHolderName,
    patientId,
    encounterId,
    billingEmail,
    isPaymentMethodValid = true,
  } = params;

  const accountHolderStatus = checkAccountHolderAddress(encounter, patient, isCreatingNewGuarantor, newGuarantorData, existingGuarantorFormData);
  const iocStatus = checkIOCComplete(encounter);
  // Use form data for demographics check when available (handles patient as guarantor with DOB in form)
  const demographicsStatus = checkDemographicsCompleteWithFormData(patient, existingGuarantorFormData);
  const billingEmailStatus = checkBillingEmailComplete(billingEmail);

  const accountHolderMessage = accountHolderName 
    ? `Information for ${accountHolderName} is incomplete`
    : disabledConvertMessages.accountHolderAddress;

  // Build fix URLs if patientId is provided
  const specialistDashboardUrl = patientId ? `/specialist/dashboard/${patientId}` : undefined;

  const requirements: PreEnrollRequirement[] = [
    {
      requirementName: accountHolderMessage,
      status: accountHolderStatus,
      missingFields: !accountHolderStatus ? getMissingAccountHolderFields(encounter, patient, isCreatingNewGuarantor, newGuarantorData, existingGuarantorFormData) : undefined,
    },
    {
      requirementName: disabledConvertMessages.iocLoadingLabel,
      status: !isLoading,
    },
    // {
    //   requirementName: disabledConvertMessages.championLabel,
    //   status: checkChampionExists(encounter),
    // },
    // {
    //   requirementName: disabledConvertMessages.paymentProgramLabel,
    //   status: checkPaymentProgramExists(encounter),
    // },
    // {
    //   requirementName: disabledConvertMessages.downPaymentLabel,
    //   status: checkDownPaymentStatus(encounter, isDownPaymentFullyPaid),
    // },
    {
      requirementName: disabledConvertMessages.recurringPaymentLabel,
      status: checkRecurringPaymentMethodExists(encounter, hasRecurringMethod),
    },
    {
      requirementName: disabledConvertMessages.achBankDetailsLabel,
      status: isPaymentMethodValid,
    },
    {
      requirementName: disabledConvertMessages.downPaymentInsufficientLabel,
      status: checkDownPaymentWillBeSatisfied(encounter, currentPaymentAmount, pendingRemainingBalance),
    },
    {
      requirementName: disabledConvertMessages.skeletonProgramLabel,
      status: checkNotSkeletonProgram(encounter),
    },
    {
      requirementName: disabledConvertMessages.iocCompletedLabel,
      status: iocStatus,
      missingFields: !iocStatus ? getMissingIOCFields(encounter) : undefined,
      fixUrl: specialistDashboardUrl,
      fixLabel: 'Complete',
    },
    {
      requirementName: disabledConvertMessages.demographicsLabel,
      status: demographicsStatus,
      missingFields: !demographicsStatus ? getMissingDemographicFieldsWithFormData(patient, existingGuarantorFormData) : undefined,
      fixUrl: specialistDashboardUrl,
      fixLabel: 'Complete',
    },
    {
      requirementName: 'Receipt email is required',
      status: billingEmailStatus,
      missingFields: !billingEmailStatus ? getMissingBillingEmailFields(billingEmail) : undefined,
    },
    // {
    //   requirementName: disabledConvertMessages.riskClassLabel,
    //   status: checkRiskClassExists(encounter),
    // },
    {
      requirementName: disabledConvertMessages.pfrMissmatchLable,
      status: checkPFRMatch(encounter),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDateLabel,
      status: checkRecurringPaymentDate(encounter, recurringPaymentDate),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDatePastLabel,
      status: checkRecurringPaymentDateNotPast(encounter, recurringPaymentDate),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDateTooFarLabel,
      status: checkRecurringPaymentDateWithin60Days(encounter, recurringPaymentDate),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDateInvalidDayLabel,
      status: checkRecurringPaymentDateValidDay(encounter, recurringPaymentDate),
    },
  ];

  const failedRequirements = requirements.filter((req) => !req.status);
  const isValid = failedRequirements.length === 0;

  return {
    isValid,
    requirements,
    failedRequirements,
  };
};

/**
 * Quick check to see if all pre-enroll requirements are met.
 * Use this for simple boolean checks without needing the full requirements list.
 */
export const arePreEnrollRequirementsMet = (
  params: PreEnrollValidationParams
): boolean => {
  return validatePreEnrollRequirements(params).isValid;
};
